import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import PaletteIcon from '@mui/icons-material/Palette';
import UndoIcon from '@mui/icons-material/Undo';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import { PhraseHighlight } from '@/components/displays/PhraseHighlight';
import { ImagePicker } from '@/components/inputs/ImagePicker/ImagePicker';
import { BUILTIN_IMG_SRC } from '@/components/inputs/ImagePicker/source';
import { WordPicker } from '@/components/inputs/WordPicker/WordPicker';
import { Popover } from '@/components/utils/Popover/Popover';
import { CANVAS_SIZE } from '@/constant';
import { SlideComponentHkcsQ2 } from '@/types/slide';
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ButtonPlaceholder } from '../components/ButtonPlaceholder';
import { ImageSelector } from '../components/ImageSelector';
import { Pronunciation } from '../components/Pronunciation';

interface Props {
    anchorEl: HTMLElement | null;
    value: SlideComponentHkcsQ2;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
    onClose: () => void;
    onUpdate?: (model: SlideComponentHkcsQ2) => void;
    onUploadImage?: (file: File) => Promise<string>;
}

export const EditPopover: React.FC<Props> = ({ anchorEl, value, slotProps, onClose, onUpdate, onUploadImage }) => {
    const { t } = useTranslation();
    const open = Boolean(anchorEl);

    // ref
    const wordPickerRef = useRef<HTMLDivElement | null>(null);
    const wordSelectorRef = useRef<HTMLElement | null>(null);
    const imagePickerRef = useRef<HTMLDivElement | null>(null);

    // ui state
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);
    const [imagePickerAnchorEl, setImagePickerAnchorEl] = useState<HTMLElement | null>(null);

    const debouncedUpdate = useDebounce(onUpdate || (() => {}), 500);

    return (
        <>
            <Popover
                title={t('slide.legacy.hkcsQ2.title')}
                open={open}
                anchorEl={anchorEl}
                onClose={() => {
                    onClose();
                }}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        ref: slotProps?.paper?.ref,
                        sx: {
                            display: 'flex',
                            p: 2,
                            width: CANVAS_SIZE.width,
                            height: CANVAS_SIZE.height,
                            overflowX: 'scroll',
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column', width: CANVAS_SIZE.width - 32 }}>
                    <Typography variant='h4' sx={{ fontSize: 24, color: 'text.secondary' }} gutterBottom>
                        {t('slide.legacy.hkcsQ2.label.description')}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row', gap: 1, height: '60%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: 236 }}>
                                <Box
                                    sx={(theme) => ({
                                        width: '100%',
                                        height: 144,
                                        padding: 1,
                                        borderRadius: 1,
                                        backgroundColor: (theme.vars || theme).palette.grey[200],
                                        ...theme.applyStyles('dark', {
                                            backgroundColor: (theme.vars || theme).palette.grey[600],
                                        }),
                                    })}
                                    ref={wordSelectorRef}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            borderRadius: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 1,
                                            height: '100%',
                                            alignItems: 'center',
                                            p: 1,
                                            position: 'relative',
                                            backgroundColor: (theme.vars || theme).palette.grey[100],
                                            ...theme.applyStyles('dark', {
                                                backgroundColor: (theme.vars || theme).palette.grey[700],
                                            }),
                                        })}
                                    >
                                        {value.content.phrase ? (
                                            <>
                                                <Box sx={{ flex: 1, alignContent: 'center' }}>
                                                    <PhraseHighlight phrase={value.content.phrase} highlight={value.content.phrase} underline={false} />
                                                </Box>
                                                <Paper sx={{ p: 1, width: '100%' }}>
                                                    {value.content?.phraseSoundGroup && (
                                                        <FormControl fullWidth>
                                                            <InputLabel id='cantonese-pronunciation-select-label'>{t('common.cantonese')}</InputLabel>
                                                            <Select
                                                                labelId='cantonese-pronunciation-select-label'
                                                                id='cantonese-pronunciation-select-id'
                                                                value={
                                                                    value.content?.phraseSoundGroup?.jyutping && value.content?.phraseSoundGroup.jyutping[0]
                                                                        ? value.content?.phraseSoundGroup.jyutping[0].startIndex.toString()
                                                                        : ''
                                                                }
                                                                label={t('common.cantonese')}
                                                                onChange={(event: SelectChangeEvent) => {
                                                                    if (value.content?.phraseSoundGroup) {
                                                                        const phraseSoundGroup = {
                                                                            ...value.content?.phraseSoundGroup,
                                                                            ...(value.content?.phraseSoundGroup?.jyutping && {
                                                                                jyutping: [{ startIndex: parseInt(event.target.value) }],
                                                                            }),
                                                                        };

                                                                        const updatedContent = {
                                                                            ...value.content,
                                                                            phraseSoundGroup: phraseSoundGroup,
                                                                        };
                                                                        const updateModel: SlideComponentHkcsQ2 = { ...value, content: updatedContent };

                                                                        debouncedUpdate(updateModel);
                                                                    }
                                                                }}
                                                            >
                                                                {value.content?.phraseSound?.jyutping?.map((_phraseSound, index) => {
                                                                    return (
                                                                        <MenuItem value={index.toString()} key={index}>
                                                                            <Stack direction='row' spacing={1} alignItems={'center'}>
                                                                                <Pronunciation
                                                                                    phraseSound={value.content?.phraseSound}
                                                                                    phrase={value.content?.phrase}
                                                                                    groupIndex={index}
                                                                                    folder={'jyutping'}
                                                                                />
                                                                                <Typography>
                                                                                    {t('slide.legacy.character.phraseSoundGroup', {
                                                                                        i: index + 1,
                                                                                    })}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Paper>

                                                <IconButton
                                                    sx={{ position: 'absolute', top: 0, right: 0, m: 0.5 }}
                                                    onClick={() => {
                                                        setWordPickerAnchorEl(wordSelectorRef.current);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </>
                                        ) : (
                                            <Button
                                                variant='contained'
                                                onClick={() => {
                                                    setWordPickerAnchorEl(wordSelectorRef.current);
                                                }}
                                                sx={{
                                                    minWidth: '50%',
                                                    bottom: 0,
                                                    left: 0,
                                                    right: 0,
                                                    m: 1,
                                                }}
                                            >
                                                {t('slide.legacy.character.selectWord')}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                                <ImageSelector
                                    onSelect={(_, selectorRef) => setImagePickerAnchorEl(selectorRef.current)}
                                    src={value.content.imageURL ? value.content.imageURL : value.content.image ? `/asset/characters/${value.content.image}/image.png` : undefined}
                                />
                            </Box>
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        border: 2,
                                        borderColor: 'divider',
                                    }}
                                />
                                <Box sx={{ width: 109, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                    <ButtonPlaceholder icon={<EditIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<PaletteIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<UndoIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                    <ButtonPlaceholder icon={<DeleteOutlineIcon sx={{ color: 'divider' }} fontSize='large' />} />{' '}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', height: '40%', pt: 1 }}>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                <ButtonPlaceholder icon={<MicIcon sx={{ color: 'divider' }} fontSize='large' />} />
                                <ButtonPlaceholder icon={<VolumeUpIcon sx={{ color: 'divider' }} fontSize='large' />} />
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateRows: 'repeat(2, 1fr)',
                                    gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '1px',
                                    backgroundColor: 'divider',
                                    height: 'fit-content',
                                    width: 'fit-content',
                                    border: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                {[...Array(14)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            height: '124px',
                                            width: '124px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'white',
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Popover>
            {imagePickerAnchorEl && (
                <ImagePicker
                    type='image'
                    title={t('slide.legacy.character.title')}
                    librarySources={BUILTIN_IMG_SRC}
                    anchorEl={imagePickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: imagePickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        value: value?.content?.image || '',
                        imageURL: value?.content?.imageURL || '',
                    }}
                    onClose={() => {
                        setImagePickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ2['content'] = {
                            ...value.content,
                            image: settings.value,
                            imageURL: settings.imageURL,
                        };
                        const updateModel = { ...value, content: updatedContent, ...(settings.size ? { size: settings.size } : {}) };

                        debouncedUpdate(updateModel);
                    }}
                    onUpload={onUploadImage}
                />
            )}
            {wordPickerAnchorEl && (
                <WordPicker
                    type='phrase'
                    title={t('slide.legacy.character.characterSearch')}
                    anchorEl={wordPickerAnchorEl}
                    slotProps={{
                        paper: {
                            ref: wordPickerRef, // Attach the ref to the Paper element
                        },
                    }}
                    value={{
                        characterList: value?.content?.characterList,
                        characterSound: value?.content?.characterSound,
                        phrase: value?.content?.phrase,
                        phraseSound: value?.content?.phraseSound,
                        phraseSoundGroup: value?.content?.phraseSoundGroup,
                    }}
                    onClose={() => {
                        setWordPickerAnchorEl(null);
                    }}
                    onUpdate={(settings) => {
                        const updatedContent: SlideComponentHkcsQ2['content'] = {
                            ...value.content,
                            character: 0, // reset character index
                            characterList: settings.characterList ?? [],
                            characterSound: settings.characterSound ?? { pinyin: [], jyutping: [] },
                            phrase: settings.phrase ?? '',
                            phraseSound: settings.phraseSound ?? { pinyin: [], jyutping: [] },
                            phraseSoundGroup: settings.phraseSoundGroup ?? { pinyin: [], jyutping: [] },
                        };
                        const updateModel: SlideComponentHkcsQ2 = { ...value, content: updatedContent };

                        debouncedUpdate(updateModel);
                    }}
                />
            )}{' '}
        </>
    );
};
